import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaFileAlt, 
  FaCrown,
  FaEnvelope,
  FaProjectDiagram,
  FaCode,
  FaLink,
  FaTicketAlt
} from 'react-icons/fa';

const AdminOverview = () => {
  const [stats, setStats] = useState({});
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const statsRes = await fetch('/api/dashboard/admin/stats', { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
        setRecentDocuments(statsData.recentDocuments || []);
        setRecentPayments(statsData.recentPayments || []);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'privacy_policy': 'Gizlilik Politikası',
      'terms_of_service': 'Kullanım Şartları',
      'cookie_policy': 'Çerez Politikası',
      'data_processing_agreement': 'Veri İşleme Sözleşmesi',
      'disclaimer': 'Sorumluluk Reddi',
      'refund_policy': 'İade Politikası',
      'acceptable_use_policy': 'Kabul Edilebilir Kullanım Politikası',
      'privacy-policy': 'Gizlilik Politikası',
      'terms-of-service': 'Kullanım Şartları',
      'cookie-policy': 'Çerez Politikası',
      'data-processing-agreement': 'Veri İşleme Sözleşmesi',
      'refund-policy': 'İade Politikası',
      'acceptable-use-policy': 'Kabul Edilebilir Kullanım Politikası'
    };
    return labels[type] || type;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'completed': 'text-green-400 bg-green-900/20 border-green-500/30',
      'pending': 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
      'failed': 'text-red-400 bg-red-900/20 border-red-500/30',
      'refunded': 'text-gray-400 bg-gray-900/20 border-gray-500/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-900/20 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Toplam Kullanıcı</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalUsers || 0}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-600/20 rounded-lg flex items-center justify-center">
              <FaUsers className="h-6 w-6 text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Toplam Döküman</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalDocuments || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center">
              <FaFileAlt className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">İletişim Mesajları</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalContactMessages || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
              <FaEnvelope className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Aktif Abonelik</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalPayments || 0}</p>
            </div>
            <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center">
              <FaCrown className="h-6 w-6 text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* New Features Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Projeler</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalProjects || 0}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-600/20 rounded-lg flex items-center justify-center">
              <FaProjectDiagram className="h-6 w-6 text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Ürünler</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalProducts || 0}</p>
            </div>
            <div className="w-12 h-12 bg-cyan-600/20 rounded-lg flex items-center justify-center">
              <FaCode className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Kısa Linkler</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalShortLinks || 0}</p>
            </div>
            <div className="w-12 h-12 bg-pink-600/20 rounded-lg flex items-center justify-center">
              <FaLink className="h-6 w-6 text-pink-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-gray-400 text-sm font-medium">Proje Destek Talepleri</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalSupportTickets || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center">
              <FaTicketAlt className="h-6 w-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-6">Son Aktiviteler</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium text-white mb-4">Son Dökümanlar</h4>
              <div className="bg-gray-700 rounded-lg p-4">
                {recentDocuments && recentDocuments.length > 0 ? (
                  <div className="space-y-3">
                    {recentDocuments.map((doc) => (
                      <div key={doc._id} className="flex justify-between items-center p-3 bg-gray-600 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-white">{doc.title}</p>
                          <p className="text-xs text-gray-400">
                            {doc.userId?.firstName} {doc.userId?.lastName} • {formatDate(doc.createdAt)}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 bg-gray-500 px-2 py-1 rounded">
                          {getDocumentTypeLabel(doc.documentType)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Son döküman bulunamadı</p>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-white mb-4">Son Ödemeler</h4>
              <div className="bg-gray-700 rounded-lg p-4">
                {recentPayments && recentPayments.length > 0 ? (
                  <div className="space-y-3">
                    {recentPayments.map((payment) => (
                      <div key={payment._id} className="flex justify-between items-center p-3 bg-gray-600 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-white">
                            ${payment.amount.toFixed(2)} {payment.currency}
                          </p>
                          <p className="text-xs text-gray-400">
                            {payment.userId?.firstName} {payment.userId?.lastName} • {formatDate(payment.createdAt)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full border ${getPaymentStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Son ödeme bulunamadı</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
